import type { LucideIcon } from 'lucide-react'

import {
    Activity,
    Flame,
    Hourglass,
    Link2,
    Puzzle,
    ShieldCheck,
    Swords,
    Timer,
    Trophy,
    Zap
} from 'lucide-react'

export type NeonAccent = 'cyan' | 'pink' | 'violet' | 'lime' | 'amber'

export const ACCENT_STYLES: Record<NeonAccent, { text: string, border: string, bg: string, hoverBorder: string, hex: string }> = {
    cyan: {
        text: 'text-neon-cyan',
        border: 'border-neon-cyan/35',
        bg: 'bg-neon-cyan/10',
        hoverBorder: 'hover:border-neon-cyan/70',
        hex: '#2ef2ff'
    },
    pink: {
        text: 'text-neon-pink',
        border: 'border-neon-pink/35',
        bg: 'bg-neon-pink/10',
        hoverBorder: 'hover:border-neon-pink/70',
        hex: '#ff3dce'
    },
    violet: {
        text: 'text-neon-violet',
        border: 'border-neon-violet/35',
        bg: 'bg-neon-violet/10',
        hoverBorder: 'hover:border-neon-violet/70',
        hex: '#9b6bff'
    },
    lime: {
        text: 'text-neon-lime',
        border: 'border-neon-lime/35',
        bg: 'bg-neon-lime/10',
        hoverBorder: 'hover:border-neon-lime/70',
        hex: '#b6ff3b'
    },
    amber: {
        text: 'text-neon-amber',
        border: 'border-neon-amber/35',
        bg: 'bg-neon-amber/10',
        hoverBorder: 'hover:border-neon-amber/70',
        hex: '#ffb547'
    }
}

export const NAV_LINKS = [
    { label: 'Features', href: '/#features' },
    { label: 'Clocks', href: '/#modes' },
    { label: 'Puzzle', href: '/#puzzle' },
    { label: 'Themes', href: '/#themes' },
    { label: 'Arenas', href: '/#arenas' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'FAQ', href: '/#faq' }
]

export const FEATURES: { icon: LucideIcon, title: string, description: string, accent: NeonAccent }[] = [
    {
        icon: Swords,
        title: "Matchmaking in seconds",
        description: "Get paired with a player near your rating in any time control, at any hour of the night.",
        accent: 'cyan'
    },
    {
        icon: Puzzle,
        title: "Puzzle Rush",
        description: "Race the clock through tactics that get sharper with every solve. Three misses and the run is over.",
        accent: 'pink'
    },
    {
        icon: Activity,
        title: "Instant game review",
        description: "Every game is analyzed the moment it ends. Your best moves, blunders and missed wins, lit up move by move.",
        accent: 'violet'
    },
    {
        icon: Trophy,
        title: "Nightly arenas",
        description: "Tournaments start every evening. Join late, play as many games as you like and climb the live leaderboard.",
        accent: 'lime'
    },
    {
        icon: Link2,
        title: "Play a friend",
        description: "Send a link, pick a clock and you're playing. Your friend doesn't even need an account.",
        accent: 'amber'
    },
    {
        icon: ShieldCheck,
        title: "Fair play, always",
        description: "Every rated game is screened for engine help, so the rating you earn is one you deserve.",
        accent: 'cyan'
    }
]

export const MODES: { name: string, clock: string, icon: LucideIcon, description: string, detail: string, accent: NeonAccent }[] = [
    {
        name: 'Bullet',
        clock: '1+0',
        icon: Zap,
        description: "One minute on the clock and no time to doubt yourself. Pure instinct.",
        detail: "About 2 minutes a game",
        accent: 'pink'
    },
    {
        name: 'Blitz',
        clock: '3+2',
        icon: Flame,
        description: "Three minutes plus two seconds a move. The sweet spot between fast and sharp.",
        detail: "About 8 minutes a game",
        accent: 'cyan'
    },
    {
        name: 'Rapid',
        clock: '10+0',
        icon: Timer,
        description: "Ten minutes to plan, calculate and punish a single slip.",
        detail: "About 20 minutes a game",
        accent: 'violet'
    },
    {
        name: 'Classical',
        clock: '30+20',
        icon: Hourglass,
        description: "The long game. Deep calculation, with time to think every move through.",
        detail: "An hour or more",
        accent: 'lime'
    }
]

export type Arena = {
    name: string
    day: string
    weekday: number
    hourUtc: number
    durationMinutes: number
    clock: string
    description: string
    featured?: boolean
}

export const ARENAS: Arena[] = [
    {
        name: 'Bullet Blackout',
        day: 'Monday',
        weekday: 1,
        hourUtc: 20,
        durationMinutes: 60,
        clock: '1+0',
        description: "An hour of one-minute chaos to kick off the week."
    },
    {
        name: 'Blitz Afterglow',
        day: 'Wednesday',
        weekday: 3,
        hourUtc: 20,
        durationMinutes: 90,
        clock: '3+2',
        description: "Midweek blitz where win streaks score double."
    },
    {
        name: 'Neon Nights Open',
        day: 'Friday',
        weekday: 5,
        hourUtc: 21,
        durationMinutes: 120,
        clock: '5+0',
        description: "The flagship arena. The top three earn this season's glowing badge.",
        featured: true
    },
    {
        name: 'Rapid Reload',
        day: 'Sunday',
        weekday: 0,
        hourUtc: 18,
        durationMinutes: 120,
        clock: '10+0',
        description: "Slower games to close out the weekend with a clear head."
    }
]

export const TESTIMONIALS = [
    {
        quote: "I came for the puzzles and stayed for the arenas. Friday nights haven't been the same since.",
        name: 'Maya R.',
        handle: '@midnightrook',
        rating: 'Blitz 1984',
        accent: 'pink' as NeonAccent
    },
    {
        quote: "The dark boards aren't a gimmick. I play after the kids are asleep and my eyes actually thank me.",
        name: 'Tomás V.',
        handle: '@hexknight',
        rating: 'Rapid 2210',
        accent: 'cyan' as NeonAccent
    },
    {
        quote: "Game review that tells me why a move was bad, not just that it was. My blitz is up 150 points since spring.",
        name: 'Priya S.',
        handle: '@lumen_pawn',
        rating: 'Blitz 1720',
        accent: 'lime' as NeonAccent
    }
]

export const PLANS = [
    {
        name: 'Free',
        price: '$0',
        period: 'forever',
        description: "Everything you need to play every single night.",
        features: [
            "Unlimited games in every time control",
            "Daily puzzle plus three Puzzle Rush runs a day",
            "Entry to every nightly arena",
            "Three instant game reviews a day",
            "All four neon board themes"
        ],
        highlighted: false,
        badge: null as string | null
    },
    {
        name: 'Neon+',
        price: '$4',
        period: 'per month',
        description: "For players chasing their next rating milestone.",
        features: [
            "Everything in Free",
            "Unlimited instant game reviews",
            "Unlimited Puzzle Rush",
            "Opening explorer for your own repertoire",
            "A glowing supporter badge next to your name",
            "New board themes before anyone else"
        ],
        highlighted: true,
        badge: "Free during the beta" as string | null
    }
]

export const FAQS = [
    {
        question: "Is Neon Gambit really free?",
        answer: "Yes. Playing, the daily puzzle and every nightly arena are free forever, with no ads. Neon+ is an optional membership for unlimited game reviews and Puzzle Rush."
    },
    {
        question: "Do I need an account to play?",
        answer: "No. Pick a level and take on the computer, or pass and play with a friend on one screen, right now in your browser. Nothing to sign up for, nothing to install."
    },
    {
        question: "Does it work on my phone?",
        answer: "It does. Neon Gambit runs in any modern browser on phone, tablet and desktop, with nothing to download or install."
    },
    {
        question: "How is my rating calculated?",
        answer: "You start with a provisional rating in each time control that settles after your first 20 games, so you're facing fair opponents within an evening."
    },
    {
        question: "How do you keep games fair?",
        answer: "Every rated game is screened for engine assistance. Accounts caught cheating are closed and their opponents get their rating points back."
    },
    {
        question: "Aren't neon boards hard on the eyes?",
        answer: "The opposite. Every theme keeps the board dark and only the pieces bright, which is far easier on tired eyes than a white screen at midnight."
    }
]
